import React, { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import posthog from "posthog-js";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { useJobStore } from "@/dentlab/src/store/Jobs";
import { Delivery, DeliveryStatusEnum } from "@/lib/types/delivery.types";
import { DeliveryStatusBadge } from "../../job-page/job-page-components/deliveries/delivery-status-badge.component";
import { DeliveryDetailDialog } from "../../job-page/job-page-components/deliveries/delivery-detail-dialog.component";
import { Logger } from "@/lib/logger/Logger";
import { Button } from "@/dentlab/src/components/ui/button";
import { RolesEnum } from "@/lib/types/roles.types";
import { X, Image } from "lucide-react";
import { getProfileDisplayName } from "@/lib/utils/client-utils";

interface DashboardDelivery extends Delivery {
	jobs?: {
		code: string;
		client_id: string;
		clients?: {
			first_name: string | null;
			last_name: string | null;
		} | null;
	} | null;
	profiles?: {
		first_name: string | null;
		last_name: string | null;
	} | null;
}

type FilterStatus = "active" | "all";

export const DashboardDeliveries: React.FC = () => {
	const role = useCentralStore((state) => state.role);
	const { updateDeliveryStatus } = useJobStore((state) => ({
		updateDeliveryStatus: state.updateDeliveryStatus,
	}));
	const [deliveries, setDeliveries] = useState<DashboardDelivery[]>([]);
	const [loading, setLoading] = useState(true);
	const [filter, setFilter] = useState<FilterStatus>("active");
	const [selectedDelivery, setSelectedDelivery] =
		useState<DashboardDelivery | null>(null);
	const isAdmin = role === RolesEnum.OWNER || role === RolesEnum.ADMIN;

	const fetchDeliveries = async () => {
		setLoading(true);

		let query = supabase
			.from("deliveries")
			.select(
				"*, jobs(code, client_id, clients(first_name, last_name)), profiles(first_name, last_name)"
			)
			.order("created_at", { ascending: false });

		if (filter === "active") {
			query = query.in("status", [
				DeliveryStatusEnum.PENDING,
				DeliveryStatusEnum.IN_TRANSIT,
			]);
		}

		const { data, error } = await query;

		if (error) {
			Logger.error(error, {}, "Error fetching dashboard deliveries");
			setLoading(false);
			return;
		}

		setDeliveries((data || []) as DashboardDelivery[]);
		setLoading(false);
	};

	useEffect(() => {
		fetchDeliveries();
	}, [filter]);

	const handleStatusChange = async (
		deliveryId: number,
		status: DeliveryStatusEnum
	) => {
		await updateDeliveryStatus(deliveryId, status);
		await fetchDeliveries();
	};

	const formatDate = (dateStr: string | null) => {
		if (!dateStr) return null;
		return new Date(dateStr).toLocaleDateString("de-CH", {
			day: "2-digit",
			month: "2-digit",
			year: "numeric",
			hour: "2-digit",
			minute: "2-digit",
		});
	};

	const statusOrder: Record<string, number> = {
		[DeliveryStatusEnum.IN_TRANSIT]: 0,
		[DeliveryStatusEnum.PENDING]: 1,
		[DeliveryStatusEnum.DELIVERED]: 2,
		[DeliveryStatusEnum.FAILED]: 3,
	};

	const sorted = [...deliveries].sort((a, b) => {
		const orderA = statusOrder[a.status] ?? 4;
		const orderB = statusOrder[b.status] ?? 4;
		if (orderA !== orderB) return orderA - orderB;
		const dateA = a.scheduled_departure_at
			? new Date(a.scheduled_departure_at).getTime()
			: Infinity;
		const dateB = b.scheduled_departure_at
			? new Date(b.scheduled_departure_at).getTime()
			: Infinity;
		return dateA - dateB;
	});

	const nextStatusMap: Partial<
		Record<
			DeliveryStatusEnum,
			{ status: DeliveryStatusEnum; label: string }
		>
	> = {
		[DeliveryStatusEnum.PENDING]: {
			status: DeliveryStatusEnum.IN_TRANSIT,
			label: "Unterwegs",
		},
		[DeliveryStatusEnum.IN_TRANSIT]: {
			status: DeliveryStatusEnum.DELIVERED,
			label: "Zugestellt",
		},
	};

	return (
		<div
			className="flex flex-col h-full"
			data-testid="dashboard-deliveries"
		>
			<div className="flex items-center justify-between mb-3">
				<div className="flex items-center gap-2">
					<button
						onClick={() => {
							setFilter("active");
							posthog.capture("delivery_filter_changed", {
								filter: "active",
							});
						}}
						className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
							filter === "active"
								? "bg-gray-900 text-white"
								: "bg-gray-100 text-gray-600 hover:bg-gray-200"
						}`}
					>
						Aktiv
					</button>
					<button
						onClick={() => {
							setFilter("all");
							posthog.capture("delivery_filter_changed", {
								filter: "all",
							});
						}}
						className={`text-xs px-2.5 py-1 rounded-full transition-colors ${
							filter === "all"
								? "bg-gray-900 text-white"
								: "bg-gray-100 text-gray-600 hover:bg-gray-200"
						}`}
					>
						Alle
					</button>
				</div>
				<span className="text-xs text-gray-400">
					{deliveries.length} Lieferungen
				</span>
			</div>

			{loading ? (
				<div className="text-center py-8 text-sm text-gray-500">
					Laden...
				</div>
			) : sorted.length === 0 ? (
				<div className="text-center py-12 text-sm text-gray-400">
					{filter === "active"
						? "Keine aktiven Lieferungen"
						: "Keine Lieferungen vorhanden"}
				</div>
			) : (
				<div className="space-y-2 overflow-auto flex-1">
					{sorted.map((delivery) => {
						const job = delivery.jobs;
						const clientName = job?.clients
							? [job.clients.last_name, job.clients.first_name]
									.filter(Boolean)
									.join(" ")
							: null;
						const courierName =
							delivery.courier_type === "person" &&
							delivery.profiles
								? getProfileDisplayName(delivery.profiles)
								: delivery.courier_company_name || null;
						const next = nextStatusMap[delivery.status];
						const isFinal =
							delivery.status === DeliveryStatusEnum.DELIVERED ||
							delivery.status === DeliveryStatusEnum.FAILED;

						return (
							<div
								key={delivery.id}
								className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 cursor-pointer transition-colors bg-white"
								onClick={() => {
									setSelectedDelivery(delivery);
									posthog.capture("delivery_detail_opened", {
										source: "dashboard",
									});
								}}
								data-testid={`dashboard-delivery-card-${delivery.id}`}
							>
								<div className="flex items-center justify-between mb-1">
									<div className="flex items-center gap-2">
										<DeliveryStatusBadge
											status={delivery.status}
										/>
										{job?.code && (
											<span className="text-xs font-mono text-gray-500">
												{job.code}
											</span>
										)}
										{delivery.file_ids &&
											delivery.file_ids.length > 0 && (
												<span className="flex items-center gap-0.5 text-xs text-gray-400">
													<Image className="h-3 w-3" />
													{delivery.file_ids.length}
												</span>
											)}
									</div>
									<div className="flex items-center gap-1.5">
										{isAdmin && !isFinal && next && (
											<Button
												variant="outline"
												size="sm"
												className="h-6 text-xs"
												onClick={(e) => {
													e.stopPropagation();
													handleStatusChange(
														delivery.id,
														next.status
													);
												}}
											>
												{next.label}
											</Button>
										)}
										{isAdmin && !isFinal && (
											<Button
												variant="outline"
												size="sm"
												className="h-6 w-6 p-0 text-red-500 border-red-200 hover:bg-red-50 hover:text-red-700"
												onClick={(e) => {
													e.stopPropagation();
													handleStatusChange(
														delivery.id,
														DeliveryStatusEnum.FAILED
													);
												}}
												aria-label="Als fehlgeschlagen markieren"
											>
												<X className="h-3.5 w-3.5" />
											</Button>
										)}
									</div>
								</div>
								<div className="flex items-center gap-3">
									{clientName && (
										<p className="text-sm font-medium text-gray-900">
											{clientName}
										</p>
									)}
									{courierName && (
										<p className="text-xs text-gray-500">
											{delivery.courier_type === "person"
												? "Kurier"
												: "Versand"}
											: {courierName}
										</p>
									)}
								</div>
								{delivery.description && (
									<p className="text-sm text-gray-600 line-clamp-1">
										{delivery.description}
									</p>
								)}
								<div className="flex items-center gap-3 mt-0.5">
									{delivery.scheduled_departure_at && (
										<p className="text-xs text-gray-500">
											Geplant:{" "}
											{formatDate(
												delivery.scheduled_departure_at
											)}
										</p>
									)}
									{delivery.delivered_at && (
										<p className="text-xs text-green-600">
											Zugestellt:{" "}
											{formatDate(delivery.delivered_at)}
										</p>
									)}
								</div>
							</div>
						);
					})}
				</div>
			)}
			<DeliveryDetailDialog
				delivery={selectedDelivery}
				open={!!selectedDelivery}
				onOpenChange={(open) => {
					if (!open) setSelectedDelivery(null);
				}}
				onFilesChanged={fetchDeliveries}
				courierName={
					selectedDelivery
						? selectedDelivery.courier_type === "person" &&
							selectedDelivery.profiles
							? getProfileDisplayName(selectedDelivery.profiles)
							: selectedDelivery.courier_company_name || undefined
						: undefined
				}
			/>
		</div>
	);
};
